import { BidiModule } from '@angular/cdk/bidi';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { UiSettingsControllerService } from '@osos/shared/api';
import {
  ConfigService,
  CoreModule,
  MarkdownModuleOptionsProvider,
} from '@osos/shared/legacy-core';
import { ManagedHttpContext } from '@osos/shared/types';
import merge from 'lodash-es/merge';
import { MarkdownModule } from 'ngx-markdown';
import {
  MonacoEditorModule,
  NGX_MONACO_EDITOR_CONFIG,
  NgxMonacoEditorConfig,
} from 'ngx-monaco-editor-v2';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appConfig } from './app.config';
import { LayoutModule } from './layout/layout.module';
import { PagesModule } from './pages/pages.module';

export function monacoConfigFactory(
  configService: ConfigService,
): NgxMonacoEditorConfig {
  const appUrl = configService.configuration?.APP_URL;
  return {
    baseUrl: `${appUrl}/assets/monaco/min/vs`,
    defaultOptions: {
      automaticLayout: true,
      scrollBeyondLastLine: false,
    },
  };
}

export class CustomLoader implements TranslateLoader {
  constructor(
    private settingService: UiSettingsControllerService,
    private http: HttpClient,
  ) {}

  public getTranslation(lang: string): Observable<any> {
    return combineLatest({
      settingTranslation: this.settingService
        .getLanguageTranslationFile({
          langCode: lang ?? 'en',
          platform: 'web',
          context: new ManagedHttpContext({
            bypassPublicMiddleware: true,
          }),
        })
        .pipe(
          switchMap((data) => data?.text() ?? of(null)),
          catchError(() => of(null)),
        ),
      assetTranslation: this.http
        .get(`../assets/i18n/common/${lang ?? 'en'}.json`)
        .pipe(catchError(() => of(null))),
    }).pipe(
      filter((data) => !!data),
      map((data) => {
        const settingTranslation = JSON.parse(data.settingTranslation);
        if (
          !settingTranslation ||
          (settingTranslation.result === null &&
            settingTranslation.success === true)
        ) {
          return data.assetTranslation ?? {};
        }
        return data.settingTranslation
          ? merge(data.assetTranslation, settingTranslation)
          : (data.assetTranslation ?? {});
      }),
      filter((data) => !!data),

      take(1),
    );
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    CoreModule,
    LayoutModule,
    PagesModule,
    BidiModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [UiSettingsControllerService, HttpClient],
      },
    }),
    MarkdownModule.forRoot({
      markedOptions: MarkdownModuleOptionsProvider,
    }),
    MonacoEditorModule.forRoot(),
  ],
  providers: [
    MessageService,
    DialogService,
    {
      provide: NGX_MONACO_EDITOR_CONFIG,
      useFactory: monacoConfigFactory,
      deps: [ConfigService],
    },
    ...appConfig.providers,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(
  settingService: UiSettingsControllerService,
  http: HttpClient,
): any {
  return new CustomLoader(settingService, http);
}
